'use client';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Trash2, Edit, Search } from 'lucide-react';
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  User,
  CreateUserData,
  UserPassword,
  UpdatePassword,
  updatePass,
} from './user';
import { Header, useModal, Onboarding, toast } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { Types as coreTypes } from '@ohif/core';
import { useAppConfig } from '@state';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

function AdminPanel({ servicesManager }) {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDialogOpenPass, setIsDialogOpenPass] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingUserPassword, setEditingUserPassword] = useState<UserPassword | null>(null);
  const [formData, setFormData] = useState<CreateUserData>({
    name: '',
    email: '',
    contraseña: '',
    role: 'user',
    vistas: ['profile', 'dashboard'],
    status: 'active',
  });
  const [formDataPass, setFormDataPass] = useState<UpdatePassword>({
    password: '',
    newpassword: '',
    newpasswordConfirmate: '',
  });

  const { t } = useTranslation();
  const { show } = useModal();
  const navigate = useNavigate();
  const [appConfig] = useAppConfig();

  const { menuOptions } = getHeaderConfig({
    servicesManager,
    t,
    show,
    navigate,
    appConfig,
  });

  const filteredUsers = users.filter(
    user =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Estado para indicar que verificamos la sesión (token) al iniciarse el componente
  const [isSessionChecked, setIsSessionChecked] = useState(false);

  useEffect(() => {
    // Verificar si hay token en localStorage
    const localStr = localStorage.getItem('usuario');
    const session = localStr ? JSON.parse(localStr) : null;

    if (!session?.token) {
      // No hay token: redirigir al login
      toast.warning('Hubo un error no tienes permisos para esta pagina');
      navigate('/');
      return;
    }

    // Ya tenemos token, podemos proceder a cargar usuarios
    setIsSessionChecked(true);
  }, [navigate]);

  useEffect(() => {
    if (!isSessionChecked) {
      return; // Esperar a que se verifique la sesión antes de hacer la petición
    }

    // Ya con el token asegurado, hacer la petición
    fetchUsers()
      .then(apiUsers => {
        const mapped: any = apiUsers.map(user => ({
          id: user._id,
          name: user.nombre,
          email: user.correo,
          role: user.role,
          vistas: user.vistas ?? [],
          status: user.status,
          createdAt: user.fechaCreacion?.split('T')[0] || '',
        }));
        setUsers(mapped);
      })
      .catch(error => {
        toast.error('Error al obtener usuarios: ' + (error as any).message);
        // Si es 401, podrías redirigir al login también
        // navigate('/login');
      });
  }, [isSessionChecked]);

  const handleCreateUser = async () => {
    try {
      const crear: any = await createUser({
        nombre: formData.name,
        correo: formData.email,
        contraseña: formData.contraseña,
        vistas: formData.vistas,
        role: formData.role,
      });
      // Recargar lista de usuarios después de crear
      const apiUsers = await fetchUsers();
      const mapped: any = apiUsers.map(user => ({
        id: user._id,
        name: user.nombre,
        email: user.correo,
        vistas: user.vistas ?? [],
        role: user.role,
        status: user.status,
        createdAt: user.fechaCreacion?.split('T')[0] || '',
      }));
      setUsers(mapped);

      toast.success(crear?.message + ' ' + crear?.user.nombre);
      setIsDialogOpen(false);
    } catch (err: any) {
      toast.error('Error al crear usuario: ' + err.message || err);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) {
      return;
    }
    try {
      const editar: any = await updateUser(editingUser.id, {
        nombre: formData.name,
        correo: formData.email,
        role: formData.role,
        vistas: formData.vistas,
      });
      toast.success(editar?.message + ' ' + editar?.user.nombre);

      setUsers(users =>
        users.map(user =>
          user.id === editingUser.id
            ? { ...user, name: formData.name, email: formData.email, role: formData.role }
            : user
        )
      );
      setIsDialogOpen(false);
    } catch (err: any) {
      toast.error('Error al actualizar usuario: ' + err.message || err);
    }
  };

  const handleUserPassword = async () => {
    if (!editingUserPassword) {
      return;
    }
    try {
      const editar: any = await updatePass(editingUserPassword.id, formDataPass);
      toast.success(editar?.message);
      setIsDialogOpenPass(false);
    } catch (err: any) {
      toast.error('Error al actualizar usuario: ' + err.message || err);
    }
  };

  const changesView = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedRole = e.target.value as typeof formData.role;
    let updatedVistas: string[] = [];

    if (selectedRole === 'traumatologia' || selectedRole === 'sala de dictados rayos x') {
      updatedVistas = ['RAYOS X'];
    } else if (selectedRole === 'pediatria') {
      updatedVistas = ['VISTA COMMON', 'DOC'];
    } else if (selectedRole === 'medicoExterno') {
      updatedVistas = ['DESCARGA DE SERIES', 'INFORMES', 'DOC', 'VISTA COMMON'];
    } else if (selectedRole === 'radiologiaInterno') {
      updatedVistas = ['DESCARGA DE SERIES', 'INFORMES', 'DOC', 'VISTA COMMON', 'MPR ADVANCED'];
    } else if (selectedRole === 'radiologiaTecnico') {
      updatedVistas = ['DESCARGA DE SERIES', 'DOC', 'VISTA COMMON'];
    } else if (selectedRole === 'enfermeria') {
      updatedVistas = ['VISTA COMMON', 'DOC'];
    } else if (selectedRole === 'administrativo') {
      updatedVistas = ['DOC'];
    } else if (selectedRole === 'emergencias') {
      updatedVistas = ['VISTA COMMON', 'DOC'];
    } else if (selectedRole === 'sala de dictados tomografia') {
      updatedVistas = ['TOMOGRAFIA'];
    } else if (selectedRole === 'medico general' || selectedRole === 'admin') {
      updatedVistas = [
        'MAMOGRAFIA',
        'TOMOGRAFIA',
        'RESONANCIA',
        'ULTRASONIDO',
        'RAYOS X',
        'DOC',
        'INFORMES',
        'LINK DE ENVIO',
        'DESCARGA DE SERIES',
        'SEND DICOM',
        'MPR ADVANCED',
        'VISTA COMMON',
      ];
    }

    setFormData({
      ...formData,
      role: selectedRole,
      vistas: updatedVistas,
    });
  };

  const handleDeleteUser = async (id: string) => {
    try {
      await deleteUser(id);
      toast.success('Usuario eliminado correctamente');
      setUsers(users => users.filter(user => user.id !== id));
    } catch (err: any) {
      toast.error('Error al eliminar el usuario: ' + err.message || err);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      contraseña: '',
      role: user.role,
      vistas: user.vistas || ['profile', 'dashboard'],
      status: user.status,
    });
    setIsDialogOpen(true);
  };

  const handleEditUserPassword = (user: UserPassword) => {
    setEditingUserPassword(user);
    setFormDataPass({
      password: user.password,
      newpassword: user.newpassword,
      newpasswordConfirmate: user.newpasswordConfirmate,
    });
    setIsDialogOpenPass(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contraseña: '',
      email: '',
      role: 'user',
      vistas: ['profile', 'dashboard'],
      status: 'active',
    });
    setEditingUser(null);
    setIsDialogOpen(false);
    setIsDialogOpenPass(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    editingUser ? handleUpdateUser() : handleCreateUser();
  };
  const handleSubmitPass = (e: React.FormEvent) => {
    e.preventDefault();
    handleUserPassword();
  };

  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Panel de Administración</h1>
            <p className="mt-1">Gestiona los usuarios del sistema</p>
          </div>
          <button
            onClick={() => {
              setEditingUser(null);
              setIsDialogOpen(true);
            }}
            className="bg-secondary-dark hover:bg-primary-dark flex items-center rounded px-4 py-2 text-white"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="30"
              height="30"
              fill="currentColor"
              className="bi bi-person-fill-add"
              viewBox="0 0 16 16"
            >
              <path d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m.5-5v1h1a.5.5 0 0 1 0 1h-1v1a.5.5 0 0 1-1 0v-1h-1a.5.5 0 0 1 0-1h1v-1a.5.5 0 0 1 1 0m-2-6a3 3 0 1 1-6 0 3 3 0 0 1 6 0" />
              <path d="M2 13c0 1 1 1 1 1h5.256A4.5 4.5 0 0 1 8 12.5a4.5 4.5 0 0 1 1.544-3.393Q8.844 9.002 8 9c-5 0-6 3-6 4" />
            </svg>
          </button>
        </div>

        {/* Buscador y lista */}
        <div className="bg-secondary-main mt-5 rounded shadow">
          <div className="flex flex-col justify-between gap-4 p-4 md:flex-row">
            <div>
              <h2 className="text-lg font-semibold">Usuarios ({filteredUsers.length})</h2>
              <p className="text-sm text-gray-400">Lista de usuarios registrados</p>
            </div>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                className="focus:ring-primary-main w-full rounded border border-gray-300 bg-black py-2 pl-10 pr-3 text-white focus:outline-none focus:ring-2"
                placeholder="Buscar usuarios..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 overflow-y-auto p-4">
            {filteredUsers.length > 0 ? (
              filteredUsers.map(user => (
                <div
                  key={user.id}
                  className="bg-secondary-dark border-primary-active gap-4 rounded border p-4 shadow max-sm:flex-col max-sm:gap-6 sm:flex sm:items-center sm:justify-between"
                >
                  <div>
                    <h3 className="text-md font-semibold">{user.name}</h3>
                    <p className="text-sm text-gray-300">{user.email}</p>
                  </div>
                  <span
                    className={`rounded px-2 py-1 text-xs uppercase ${user.role === 'admin' ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-800'}`}
                  >
                    {user.role}
                  </span>
                  <span
                    className={`rounded px-2 py-1 text-xs ${user.status === 'active' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}
                  >
                    {user.status === 'active' ? 'Activo' : 'Inactivo'}
                  </span>
                  <p className="text-xs text-gray-400">Registrado: {user.createdAt}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        handleEditUserPassword({
                          id: user.id,
                          password: '',
                          newpassword: '',
                          newpasswordConfirmate: '',
                        })
                      }
                      className="text-yellow-400 hover:text-yellow-700"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-key-fill"
                        viewBox="0 0 16 16"
                      >
                        <path d="M3.5 11.5a3.5 3.5 0 1 1 3.163-5H14L15.5 8 14 9.5l-1-1-1 1-1-1-1 1-1-1-1 1H6.663a3.5 3.5 0 0 1-3.163 2M2.5 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleEditUser(user)}
                      className="text-blue-300 hover:text-blue-600"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="text-red-300 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="py-8 text-center text-gray-400">No se encontraron usuarios</p>
            )}
            <div className="bg-secondary-dark border-primary-active gap-4 rounded border p-4 shadow max-sm:flex-col max-sm:gap-6 sm:hidden sm:items-center sm:justify-between">
              <div></div>
              <span></span>
              <span></span>
              <div className="flex gap-2"></div>
            </div>
          </div>
        </div>

        {isDialogOpenPass && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-primary-dark max-w-md rounded p-6 shadow-lg sm:w-full">
              <h3 className="mb-1 text-lg font-semibold">Actualizar Contraseña</h3>
              <form
                onSubmit={handleSubmitPass}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium">Contraseña</label>
                  <input
                    type="password"
                    name="password"
                    value={formDataPass.password}
                    onChange={e => setFormDataPass({ ...formDataPass, password: e.target.value })}
                    className={
                      'focus:ring-primary-main w-full rounded border bg-black px-3 py-2 focus:outline-none focus:ring'
                    }
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Nueva contraseña</label>
                  <input
                    type="password"
                    name="newpassword"
                    value={formDataPass.newpassword}
                    onChange={e =>
                      setFormDataPass({ ...formDataPass, newpassword: e.target.value })
                    }
                    className="focus:ring-primary-main w-full rounded border bg-black px-3 py-2 focus:outline-none focus:ring"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Confirmar Contraseña</label>
                  <input
                    type="password"
                    name="newpasswordConfirmate"
                    value={formDataPass.newpasswordConfirmate}
                    onChange={e =>
                      setFormDataPass({ ...formDataPass, newpasswordConfirmate: e.target.value })
                    }
                    className={
                      'focus:ring-primary-main w-full rounded border bg-black px-3 py-2 focus:outline-none focus:ring'
                    }
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded bg-red-700 px-4 py-2 text-white hover:bg-red-500"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-900"
                  >
                    {'Actualizar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {isDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-primary-dark max-w-md rounded p-6 shadow-lg sm:w-full">
              <h3 className="mb-1 text-lg font-semibold">
                {editingUser ? 'Editar Usuario' : 'Crear Usuario'}
              </h3>
              <form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium">Nombre</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className={
                      'focus:ring-primary-main w-full rounded border bg-black px-3 py-2 focus:outline-none focus:ring'
                    }
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    className="focus:ring-primary-main w-full rounded border bg-black px-3 py-2 focus:outline-none focus:ring"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium">Contraseña</label>
                  <input
                    type="password"
                    value={formData.contraseña}
                    onChange={e => setFormData({ ...formData, contraseña: e.target.value })}
                    className={
                      editingUser
                        ? 'hidden'
                        : 'focus:ring-primary-main w-full rounded border bg-black px-3 py-2 focus:outline-none focus:ring'
                    }
                  />
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium">Rol</label>
                    <select
                      value={formData.role}
                      onChange={changesView}
                      className="focus:ring-primary-main bg-primary-main w-full rounded border px-3 py-2 focus:ring"
                    >
                      <option value="user">Usuario</option>
                      <option value="administrativo">Administrativo</option>
                      <option value="admin">Administrador</option>
                      <option value="enfermeria">Enfermeria</option>
                      <option value="emergencias">Emergencias</option>
                      <option value="traumatologia">Traumatologia</option>
                      <option value="pediatria">Pediatria</option>
                      <option value="radiologiaTecnico">Tecnico Radiologo</option>
                      <option value="radiologiaInterno">Medico Radiologo Interno</option>
                      <option value="medicoExterno">Medico externo</option>
                      <option value="sala de dictados rayos x">Sala de dictados rayos x</option>
                      <option value="sala de dictados tomografia">
                        Sala de dictados tomografia
                      </option>
                      <option value="emergencias">Emergencias</option>
                      <option value="medico general">Medico General</option>
                    </select>
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium">Estado</label>
                    <select
                      value={formData.status}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          status: e.target.value as 'active' | 'inactive',
                        })
                      }
                      className="focus:ring-primary-main bg-primary-main w-full rounded border px-3 py-2 focus:ring"
                    >
                      <option value="active">Activo</option>
                      <option value="inactive">Inactivo</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Vistas</label>
                  <div className="flex flex-wrap gap-3">
                    {[
                      'MAMOGRAFIA',
                      'TOMOGRAFIA',
                      'RESONANCIA',
                      'ULTRASONIDO',
                      'RAYOS X',
                      'DOC',
                      'INFORMES',
                      'LINK DE ENVIO',
                      'DESCARGA DE SERIES',
                      'SEND DICOM',
                      'MPR ADVANCED',
                      'VISTA COMMON',
                    ].map(vista => (
                      <label
                        key={vista}
                        className="flex items-center space-x-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          value={vista}
                          checked={formData.vistas.includes(vista)}
                          onChange={e => {
                            const checked = e.target.checked;
                            setFormData(prev => ({
                              ...prev,
                              vistas: checked
                                ? [...prev.vistas, vista]
                                : prev.vistas.filter(v => v !== vista),
                            }));
                          }}
                        />
                        <span className="text-sm">{vista}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded bg-red-700 px-4 py-2 text-white hover:bg-red-500"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-900"
                  >
                    {editingUser ? 'Actualizar' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function getHeaderConfig({ servicesManager, t, show, navigate, appConfig }) {
  const { customizationService } = servicesManager.services;

  const AboutModal = customizationService.getCustomization(
    'ohif.aboutModal'
  ) as coreTypes.MenuComponentCustomization;
  const UserPreferencesModal = customizationService.getCustomization(
    'ohif.userPreferencesModal'
  ) as coreTypes.MenuComponentCustomization;

  const menuOptions = [
    {
      title: AboutModal?.menuTitle ?? t('Header:About'),
      icon: 'info',
      onClick: () =>
        show({
          content: AboutModal,
          title: AboutModal?.title ?? t('AboutModal:About MED VIEWER'),
          containerClassName: AboutModal?.containerClassName ?? 'max-w-md',
        }),
    },
    {
      title: UserPreferencesModal?.menuTitle ?? t('Header:Preferences'),
      icon: 'settings',
      onClick: () =>
        show({
          content: UserPreferencesModal,
          title: UserPreferencesModal?.title ?? t('UserPreferencesModal:User preferences'),
          containerClassName:
            UserPreferencesModal?.containerClassName ?? 'flex max-w-4xl p-6 flex-col',
        }),
    },
  ];

  if (appConfig.oidc) {
    menuOptions.push({
      icon: 'power-off',
      title: t('Header:Logout'),
      onClick: () => {
        navigate(`/logout?redirect_uri=${encodeURIComponent(window.location.href)}`);
      },
    });
  }

  return { menuOptions, appConfig };
}

AdminPanel.propTypes = {
  isLoadingData: PropTypes.bool,
  servicesManager: PropTypes.object.isRequired,
};

export default AdminPanel;
